// ConvoCX unified inbox — conversation thread + reply composer (CON-845).
// Middle pane: message list with read receipts, plus the reply composer.

import { Fragment, useEffect, useRef } from 'react';
import {
  Avatar,
  Button,
  ChatComposer,
  ChatMessage,
  ChatMessageBubble,
  ChatMessageList,
  ChatMessageMetadata,
  Divider,
  EmptyState,
} from '@astryxdesign/core';
import type { Conversation, Message } from './types';

function senderOf(m: Message): 'user' | 'assistant' {
  return m.direction === 'outbound' ? 'user' : 'assistant';
}

function messageStatus(m: Message): 'read' | 'delivered' | 'sent' {
  if (m.read_at) return 'read';
  if (m.direction === 'outbound') return 'delivered';
  return 'delivered';
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

function StarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

interface ThreadProps {
  conversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  sending: boolean;
  onSend: (body: string) => void;
  onBack?: () => void;
  canSend: boolean;
  onToggleStar?: () => void;
  onToggleArchive?: () => void;
}

export function ConversationThread({ conversation, messages, loading, sending, onSend, onBack, canSend, onToggleStar, onToggleArchive }: ThreadProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length, conversation?.id]);

  if (!conversation) {
    return (
      <div className="ccx-inbox__thread" data-testid="inbox-thread">
        <div className="ccx-thread__empty">
          <EmptyState title="Select a conversation" description="Choose a conversation from the queue to view the thread and reply." />
        </div>
      </div>
    );
  }

  const participant = (conversation.participant_data ?? {}) as { name?: string; email?: string; phone?: string };
  const displayName = participant.name || participant.email || conversation.subject || 'Conversation';

  return (
    <div className="ccx-inbox__thread" data-testid="inbox-thread">
      <div className="ccx-thread__head">
        {onBack && (
          <span className="ccx-thread__back">
            <Button label="Back to inbox" variant="ghost" size="sm" onClick={onBack} />
          </span>
        )}
        <Avatar name={displayName} size="md" />
        <div style={{ minWidth: 0 }}>
          <div className="ccx-thread__name">{displayName}</div>
          <div className="ccx-thread__meta">
            <span className={`ccx-status ccx-status--${conversation.status}`}>{conversation.status}</span>
            {conversation.channel_name && <span>{conversation.channel_name}</span>}
            {(conversation.unread_count ?? 0) > 0 && <span>{conversation.unread_count} unread</span>}
          </div>
        </div>
        <div className="ccx-thread__actions">
          {onToggleStar && (
            <Button
              label={conversation.is_starred ? 'Unstar conversation' : 'Star conversation'}
              icon={<StarIcon />}
              isIconOnly
              variant={conversation.is_starred ? 'primary' : 'ghost'}
              size="sm"
              onClick={onToggleStar}
              data-testid="thread-star"
              aria-pressed={conversation.is_starred}
            />
          )}
          {onToggleArchive && (
            <Button
              label={conversation.is_archived ? 'Unarchive conversation' : 'Archive conversation'}
              icon={<ArchiveIcon />}
              isIconOnly
              variant="ghost"
              size="sm"
              onClick={onToggleArchive}
              data-testid="thread-archive"
              aria-pressed={conversation.is_archived}
            />
          )}
        </div>
      </div>

      <div className="ccx-thread__body" ref={listRef} data-testid="inbox-thread-messages">
        {loading && messages.length === 0 && (
          <div className="ccx-thread__state">Loading messages…</div>
        )}

        {!loading && messages.length === 0 && (
          <EmptyState title="No messages yet" description="This conversation has no messages. Start the conversation below." />
        )}

        {!loading && messages.length > 0 && (
          <ChatMessageList gap={2} density="compact">
            {messages.map((m, i) => {
              const sender = senderOf(m);
              const senderInfo = (m.sender_data ?? {}) as { name?: string; type?: string };
              const prev = messages[i - 1];
              const isNewDay = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
              return (
                <Fragment key={m.id}>
                  {isNewDay && (
                    <Divider className="ccx-thread__day" label={dayLabel(m.created_at)} />
                  )}
                  <ChatMessage
                    sender={sender}
                    avatar={<Avatar name={senderInfo.name ?? (m.direction === 'outbound' ? 'You' : displayName)} size="md" />}
                  >
                    <ChatMessageBubble
                      name={m.direction === 'outbound' ? 'You' : senderInfo.name ?? displayName}
                      metadata={
                        <ChatMessageMetadata
                          timestamp={formatTime(m.created_at)}
                          status={messageStatus(m)}
                        />
                      }
                    >
                      {m.body || ''}
                    </ChatMessageBubble>
                  </ChatMessage>
                </Fragment>
              );
            })}
          </ChatMessageList>
        )}
      </div>

      <div className="ccx-thread__composer" data-testid="inbox-composer">
        <ChatComposer
          onSubmit={onSend}
          isDisabled={!canSend || sending}
          placeholder={canSend ? 'Type your reply…' : 'Reconnecting…'}
          density="compact"
          status={sending ? { type: 'warning', message: 'Sending…' } : undefined}
        />
      </div>
    </div>
  );
}